import { Box } from '@mui/material'
import {
    GridColDef,
    GridColumnVisibilityModel,
    GridPaginationMeta,
    GridPaginationModel,
    GridValidRowModel,
} from '@mui/x-data-grid'
import { useMemo, useRef, useState } from 'react'

import { StyledDataGrid } from '../../../../styles/styledComponents/styledDataGrid'

interface DataGridProps {
    rows: GridValidRowModel[]
    columns: GridColDef<GridValidRowModel>[]
    hasNextPage?: boolean
    isLoading: boolean
    onPaginationChange: (page: number, pageSize: number) => void
    pagination: { page: number; pageSize: number }
    noRowsLabel?: string
    initialColumnVisibilityModel?: GridColumnVisibilityModel
}

export const DataGridWithPagination = ({
    rows,
    columns,
    hasNextPage,
    isLoading,
    onPaginationChange,
    pagination,
    noRowsLabel = 'No experiments found',
    initialColumnVisibilityModel = { id: false, expId: false },
}: DataGridProps) => {
    const [columnVisibilityModel, setColumnVisibilityModel] =
        useState<GridColumnVisibilityModel>(initialColumnVisibilityModel)

    const handlePaginationChange = (newPaginationModel: GridPaginationModel) => {
        onPaginationChange(newPaginationModel.page, newPaginationModel.pageSize)
    }

    const paginationMetaRef = useRef<GridPaginationMeta>()

    const paginationMeta = useMemo(() => {
        if (hasNextPage !== undefined) {
            paginationMetaRef.current = { hasNextPage }
        }
        return paginationMetaRef.current ?? { hasNextPage: false }
    }, [hasNextPage])

    const rowCount = paginationMeta.hasNextPage ? -1 : pagination.page * pagination.pageSize + rows.length

    return (
        <Box sx={{ height: '100%', minHeight: '600px' }}>
            <StyledDataGrid
                data-test="data-grid-with-pagination"
                columns={columns}
                rows={rows}
                loading={isLoading}
                localeText={{ noRowsLabel }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                pageSizeOptions={[5, 10, 25, 30]}
                paginationMode="server"
                paginationModel={pagination}
                rowCount={rowCount}
                onPaginationModelChange={handlePaginationChange}
                paginationMeta={paginationMeta}
            />
        </Box>
    )
}
